"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readNonNegative, readQuantity } from "@/lib/tools/numeric-input"

const FALLBACK_RELATED = [
  "energy-cost-calculator",
  "refrigerator-size-calculator",
  "kitchen-space-planner",
  "wine-refrigerator-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Kegerator Keg Yield Calculator",
  category: "Size & Capacity" as const,
  mediumDescription:
    "Calculate exactly how many pours come out of any keg size, factoring serving size and pour wastage. Includes 12 oz can-equivalent and pour-quality tips.",
  faqs: [
    {
      question: "What's a typical pour wastage percentage?",
      answer:
        "A well-tuned kegerator with proper CO₂ pressure (10–14 PSI) and clean lines wastes 5–10%. Anything above 15% means foam problems — usually pressure too high or temperature too warm.",
    },
    {
      question: "How long does a keg stay fresh once tapped?",
      answer:
        "A pressurized, properly chilled keg (36–38°F) stays good 2–3 months. Once tapped and exposed to outside air, plan to consume within 1–2 months for best flavor.",
    },
    {
      question: "What's the right serving glass size?",
      answer:
        "12 oz matches a standard can. 16 oz is the American pint. 20 oz is the imperial pint used in UK/Ireland. The math just divides total ounces by your serving size.",
    },
    {
      question: "Why are sixth-barrel kegs so popular for home use?",
      answer:
        "Sixtels (5.16 gallons / ~661 oz) fit most undercounter kegerators, weigh half what a full keg does, and yield ~40 pints. Easier to handle, easier to swap.",
    },
    {
      question: "Does pouring technique really matter that much?",
      answer:
        "Yes. A 45° glass tilt and a clean, properly-chilled glass cuts foam waste roughly in half compared to a vertical pour into a warm glass.",
    },
  ],
  relatedTools: ["refrigerator-size-calculator", "energy-cost-calculator"],
}

const KEG_SIZES: Record<string, { gallons: number; cost: number; label: string }> = {
  "half-barrel": { gallons: 15.5, cost: 100, label: "Half barrel (15.5 gal)" },
  "quarter-barrel": { gallons: 7.75, cost: 60, label: "Quarter barrel (7.75 gal)" },
  "sixth-barrel": { gallons: 5.16, cost: 50, label: "Sixth barrel / sixtel (5.16 gal)" },
  "cornelius": { gallons: 5, cost: 45, label: "Cornelius / corny (5 gal)" },
  "mini": { gallons: 1.32, cost: 20, label: "Mini keg (1.32 gal)" },
}

export function KegYieldCalculatorPageClient() {
  const [kegSize, setKegSize] = useState("half-barrel")
  const [servingSize, setServingSize] = useState("16")
  const [wastage, setWastage] = useState("10")

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    // A pour glass runs 1 oz (taster) to 64 (growler fill). Wastage is a share
    // of the keg, so 0 is a real reading and 100% is the ceiling.
    const serving = readQuantity(servingSize, { min: 1, max: 64 })
    const waste = readNonNegative(wastage, { max: 100 })
    if (serving === null || waste === null) return null

    const config = KEG_SIZES[kegSize]
    if (config === undefined) return null
    const kegGallons = config.gallons
    const kegOunces = kegGallons * 128

    const totalServings = Math.floor(kegOunces / serving)
    const wastedOunces = kegOunces * (waste / 100)
    const usableOunces = kegOunces - wastedOunces
    const usableServings = Math.floor(usableOunces / serving)

    const equivalentCans = Math.floor(kegOunces / 12)
    const equivalentCases = parseFloat((equivalentCans / 24).toFixed(1))

    // 100% wastage — or any wastage that leaves less than one pour — yields no
    // servings, and `cost / 0` is Infinity, which rendered "$Infinity". A keg
    // that pours nothing has no cost per pour; the figure is absent, not huge.
    const costPerServing =
      usableServings > 0 ? parseFloat((config.cost / usableServings).toFixed(2)) : null

    return {
      kegGallons,
      kegOunces: Math.round(kegOunces),
      totalServings,
      usableServings,
      wastedOunces: Math.round(wastedOunces),
      costPerServing,
      equivalentCases,
      kegLabel: config.label,
    }
  }, [kegSize, servingSize, wastage])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="How many pours"
      heroTitleItalic="actually"
      heroTitlePost="come out of a keg?"
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~15 sec", "Industry pour-yield rules"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "128", label: "Oz per gallon", note: "US measure" },
            { value: "5–10%", label: "Healthy wastage", note: "Per pour" },
            { value: "36–38°F", label: "Ideal keg temp", note: "Cold side" },
            { value: "10–14", label: "PSI for CO₂", note: "Standard" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "kegerator-keg-yield-calculator")}
      cta={{
        title: "Cold beer needs the right cold box.",
        body: "Browse beverage centers and undercounter refrigerators sized for kegerator conversions and home bars.",
        buttonLabel: "Browse beverage centers",
        buttonHref: "/collections/beverage-coolers",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Keg, glass,
                <br />
                pour quality.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Wastage above 15% almost always means CO₂ or temperature problems.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Keg size
                  </label>
                  <select
                    value={kegSize}
                    onChange={(e) => setKegSize(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    {Object.entries(KEG_SIZES).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Serving size
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={1}
                      max={64}
                      step="1"
                      value={servingSize}
                      onChange={(e) => setServingSize(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">oz</span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> 12 = can · 16 = US pint · 20 = imperial pint
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Pour wastage
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step="1"
                      value={wastage}
                      onChange={(e) => setWastage(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">%</span>
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }}
                />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Usable pours
                  </div>
                  <div
                    className="font-black"
                    style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}
                  >
                    {result?.usableServings ?? 0}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">pours</span>
                  </div>
                  <div className="mt-4 text-[15px] text-white/85 leading-[1.5]">
                    {result ? `≈ ${result.equivalentCases} cases (24 × 12 oz cans)` : "Enter keg details."}
                  </div>
                  <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Total oz</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.kegOunces ?? "—"}</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Wasted oz</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.wastedOunces ?? "—"}</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">$ / pour</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.costPerServing != null ? `$${result.costPerServing}` : "—"}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Cold infrastructure"
          title="Counter-depth fridges for kegerator conversions"
          description="Undercounter cold boxes with adjustable shelving — perfect base units for tap setups."
          collectionType="counter-depth"
          fallbackCollectionType="counter-depth"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                <em className="italic text-[#ff9951]">128</em> ounces per gallon. Honest math.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                US standard keg sizes and pour-yield rules from Brewers Association sizing guidance, with cost references typical of a US wholesale market.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Total fluid ounces",
                  d: "Keg gallons × 128 oz/gal. Half-barrel = 1,984 oz, sixtel = 660, mini = 169.",
                },
                {
                  n: "02",
                  t: "Wastage subtraction",
                  d: "Multiply total oz by your wastage % (typically 5–15%) and subtract before dividing into pours.",
                },
                {
                  n: "03",
                  t: "Pours and case-equivalents",
                  d: "Usable oz ÷ serving size = pours. Total oz ÷ 12 ÷ 24 = case-equivalent (24-pack).",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Counter-depth fridges that work as a home bar"
          description="Built-in or freestanding 24-inch units — ideal companion cold storage to a tap setup."
          collectionType="counter-depth"
          fallbackCollectionType="counter-depth"
        />
      </section>
    </ToolPageShell>
  )
}
